import React, {useContext} from 'react';
import { useAppStore } from '../../../GlobalStore/GlobalStore';
import {GlobalContext} from "../../../App.tsx";
import {requestProjectFiles} from "../../../Utils/jsBridge/sender/ide/requestProjectFiles.ts";

interface EmbeddingStatusProps {}

const EmbeddingStatus: React.FC<EmbeddingStatusProps> = () => {
    const { websocketRef } = useContext(GlobalContext);
    const dynamicStateObj = useAppStore((state: any) => state.dynamicStateObj);
    const setDynamicStateObj = useAppStore((state: any) => state.setDynamicStateObj);
    const setEnvironmentObj = useAppStore((state: any) => state.setEnvironmentObj);
    const environmentObj = useAppStore((state: any) => state.environmentObj);
    const { environment, vscode } = environmentObj;
    const { embeddingInfo } = dynamicStateObj;
    const { status: embeddingStatus, error: embeddingError, progress: embeddingProgress } = embeddingInfo;

    // Don't render if the error is about no files needing embedding updates
    if (embeddingStatus === "error" && embeddingError?.includes("No files found that need their embeddings updated")) {
        return null;
    }

    const handleCancel = async () => {
        websocketRef.current.send(JSON.stringify({
            functionName: 'stopEmbeddings',
            args: {}
        }));

        setEnvironmentObj({ projectEmbeddingsLastUpdated: null });

        setDynamicStateObj({ embeddingInfo: { status: null, error: null, progress: 0 } });
    };

    const handleRetry = () => {
        setDynamicStateObj({ embeddingInfo: { status: null, error: null, progress: 0 } });
        requestProjectFiles(environment, vscode);
    };

    const handleDismiss = () => {
        setDynamicStateObj({ embeddingInfo: { status: null, error: null, progress: 0 } });
    };

    const isComplete = embeddingProgress === 100;

    return (
        <div className="w-full">
            <div className="bg-neutral-300/50 dark:bg-neutral-800/50 rounded-lg w-full overflow-hidden border border-neutral-700/30 relative">
                <div className="p-3">
                    <div className="flex flex-col items-center space-y-2">
                        {embeddingStatus === "error" ? (
                            <>
                                <div className="flex items-center gap-2">
                                    <div className="h-5 w-5 rounded-full bg-red-500 flex items-center justify-center">
                                        <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            className="h-3 w-3 text-white"
                                            fill="none"
                                            viewBox="0 0 24 24"
                                            stroke="currentColor"
                                        >
                                            <path
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                strokeWidth={2}
                                                d="M6 18L18 6M6 6l12 12"
                                            />
                                        </svg>
                                    </div>
                                    <p className="text-red-600 dark:text-red-400 text-lg font-medium">
                                        {embeddingError}
                                    </p>
                                </div>
                                <div className="flex gap-2">
                                    <button
                                        onClick={handleRetry}
                                        className="px-3 py-1.5 bg-cyan-800 text-white rounded hover:bg-cyan-700 transition-colors text-base"
                                    >
                                        Retry
                                    </button>
                                    <button
                                        onClick={handleDismiss}
                                        className="px-3 py-1.5 bg-neutral-800 text-white rounded hover:bg-neutral-700 transition-colors text-base"
                                    >
                                        Dismiss
                                    </button>
                                </div>
                            </>
                        ) : (
                            <>
                                <div className="flex items-center gap-3">
                                    {isComplete ? (
                                        <div className="h-5 w-5 rounded-full bg-green-500 flex items-center justify-center">
                                            <svg
                                                xmlns="http://www.w3.org/2000/svg"
                                                className="h-3 w-3 text-white"
                                                fill="none"
                                                viewBox="0 0 24 24"
                                                stroke="currentColor"
                                            >
                                                <path
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                    strokeWidth={2}
                                                    d="M5 13l4 4L19 7"
                                                />
                                            </svg>
                                        </div>
                                    ) : (
                                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-cyan-500"></div>
                                    )}
                                    <p className="text-neutral-600 dark:text-neutral-300 text-lg font-medium">
                                        {isComplete ? 'Complete!' : `Progress: ${embeddingProgress}%`}
                                    </p>
                                </div>

                                {!isComplete && (
                                    <>
                                        <div className="w-full bg-neutral-200 dark:bg-neutral-700 rounded-full h-1.5">
                                            <div
                                                className="bg-cyan-500 h-1.5 rounded-full transition-all duration-300 ease-in-out"
                                                 style={{ width: `${embeddingProgress}%` }}
                                            ></div>
                                        </div>
                                        <button
                                            onClick={handleCancel}
                                            className="px-2 py-1 bg-red-800 text-white rounded hover:bg-red-700 transition-colors text-sm"
                                        >
                                            Cancel
                                        </button>
                                    </>
                                )}
                            </>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default EmbeddingStatus;
